import { createApiRef, IdentityApi } from '@backstage/core-plugin-api';

export async function createExecSocket(
  baseUrl: string,
  identity: Pick<IdentityApi, 'getCredentials'>,
  signal?: AbortSignal,
): Promise<WebSocket> {
  const { token } = await identity.getCredentials();
  if (signal?.aborted) throw new Error('EKS terminal connection cancelled');
  if (!token) throw new Error('Sign in to open an EKS terminal');
  const url = new URL(`${baseUrl.replace(/\/$/, '')}/api/eks/exec`);
  if (!['http:', 'https:'].includes(url.protocol))
    throw new Error('Invalid EKS backend URL');
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return new WebSocket(url.toString(), [
    'backstage-eks-exec',
    `backstage-token.${token}`,
  ]);
}

export interface Cluster {
  name: string;
  arn: string;
  status: string;
  version: string;
  endpoint: string;
  createdAt: string;
}

export interface Pod {
  name: string;
  namespace: string;
  status: string;
  ready: string;
  restarts: number;
  age: string;
  containers: Container[];
}

export interface Container {
  name: string;
  image: string;
  ready: boolean;
}

export interface Namespace {
  name: string;
  status: string;
}

export interface EKSApi {
  getClusters(accountId: string): Promise<Cluster[]>;
  getPods(
    accountId: string,
    clusterName: string,
    namespace: string,
  ): Promise<Pod[]>;
  getNamespaces(accountId: string, clusterName: string): Promise<Namespace[]>;
  createExecSession(
    accountId: string,
    clusterName: string,
    namespace: string,
    podName: string,
    containerName: string,
  ): Promise<WebSocket>;
}

export const eksApiRef = createApiRef<EKSApi>({
  id: 'plugin.eks.service',
});

export class EKSClient implements EKSApi {
  private readonly baseUrl: string;

  constructor(
    baseUrl: string,
    private readonly identity: Pick<IdentityApi, 'getCredentials'>,
  ) {
    this.baseUrl = baseUrl;
  }

  async getClusters(accountId: string): Promise<Cluster[]> {
    const response = await fetch(
      `${this.baseUrl}/api/eks/accounts/${accountId}/clusters`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch clusters: ${response.statusText}`);
    }

    return response.json();
  }

  async getPods(
    accountId: string,
    clusterName: string,
    namespace: string,
  ): Promise<Pod[]> {
    const response = await fetch(
      `${this.baseUrl}/api/eks/accounts/${accountId}/clusters/${clusterName}/namespaces/${namespace}/pods`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch pods: ${response.statusText}`);
    }

    return response.json();
  }

  async getNamespaces(
    accountId: string,
    clusterName: string,
  ): Promise<Namespace[]> {
    const response = await fetch(
      `${this.baseUrl}/api/eks/accounts/${accountId}/clusters/${clusterName}/namespaces`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch namespaces: ${response.statusText}`);
    }

    return response.json();
  }

  async createExecSession(
    _accountId: string,
    clusterName: string,
    namespace: string,
    podName: string,
    containerName: string,
  ): Promise<WebSocket> {
    const socket = await createExecSocket(this.baseUrl, this.identity);
    socket.addEventListener(
      'open',
      () => {
        socket.send(
          JSON.stringify({
            type: 'connect',
            clusterName,
            namespace,
            podName,
            containerName,
          }),
        );
      },
      { once: true },
    );
    return socket;
  }
}
